import React, { useState, useEffect, useRef, useCallback } from 'react';
import { DndContext, DragOverlay, PointerSensor, TouchSensor, useSensor, useSensors, closestCenter, pointerWithin } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import apiClient from '../../../utils/api';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { toast } from 'sonner';
import { Loader2, Save, Film, Image, Video, ArrowLeft, Sparkles, Home, Trophy, Undo2, Redo2, Send, Layers, Palette, Music, ChevronDown, Clock } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Timeline } from './Timeline';
import { getEffDur } from './timeline/constants';
import { AssetPanel } from './AssetPanel';
import { PreviewPlayer } from './PreviewPlayer';
import { LayerPanel } from './LayerPanel';
import { PropertyInspector } from './PropertyInspector';
import { DesignPicker } from './DesignPicker';
import { AudioMixer } from './AudioMixer';
import { ReelTemplatePicker } from './ReelTemplatePicker';
import { PublishDialog } from './PublishDialog';
import CanvaTextToolbar from './FloatingTextToolbar';
import { usePlan } from '../../../contexts/PlanContext';
import { useIsMobile } from '../../../hooks/useIsMobile';
import MobileCoachMark from '../../MobileCoachMark';

// Memoized panels — they must not re-render on 10Hz playback time ticks.
const MAssetPanel = React.memo(AssetPanel);
const MLayerPanel = React.memo(LayerPanel);
const MPropertyInspector = React.memo(PropertyInspector);
const MDesignPicker = React.memo(DesignPicker);
const MAudioMixer = React.memo(AudioMixer);

// ========== Undo / Redo Hook ==========
const MAX_HISTORY = 50;

function useHistory(initialState) {
  const [state, setState] = useState(initialState);
  const pastRef = useRef([]);
  const futureRef = useRef([]);
  const skipRef = useRef(false);

  const set = useCallback((updater) => {
    setState(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      if (!skipRef.current) {
        pastRef.current = [...pastRef.current.slice(-MAX_HISTORY), JSON.parse(JSON.stringify(prev))];
        futureRef.current = [];
      }
      skipRef.current = false;
      return next;
    });
  }, []);

  const setQuiet = useCallback((updater) => {
    skipRef.current = true;
    setState(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      pastRef.current = [];
      futureRef.current = [];
      return next;
    });
  }, []);

  // Update state WITHOUT touching history — used for high-frequency live
  // drag/resize/rotate moves. Pair with beginChange() (called once at the
  // start of a gesture) so one gesture = one undo step.
  const setLive = useCallback((updater) => {
    setState(prev => (typeof updater === 'function' ? updater(prev) : updater));
  }, []);

  // Snapshot the current state into the undo stack once, without mutating it.
  const beginChange = useCallback(() => {
    setState(prev => {
      pastRef.current = [...pastRef.current.slice(-MAX_HISTORY), JSON.parse(JSON.stringify(prev))];
      futureRef.current = [];
      return prev; // same reference → no re-render
    });
  }, []);

  const undo = useCallback(() => {
    if (pastRef.current.length === 0) return;
    setState(prev => {
      futureRef.current = [JSON.parse(JSON.stringify(prev)), ...futureRef.current.slice(0, MAX_HISTORY)];
      const restored = pastRef.current.pop();
      return restored;
    });
  }, []);

  const redo = useCallback(() => {
    if (futureRef.current.length === 0) return;
    setState(prev => {
      pastRef.current = [...pastRef.current, JSON.parse(JSON.stringify(prev))];
      const restored = futureRef.current.shift();
      return restored;
    });
  }, []);

  return { state, set, setQuiet, setLive, beginChange, undo, redo, pastRef, futureRef };
}

const TPL_ICONS = [
  { id: 'sparkles', Icon: Sparkles },
  { id: 'home', Icon: Home },
  { id: 'trophy', Icon: Trophy },
  { id: 'film', Icon: Film },
];
const TPL_CATEGORIES = [
  { id: 'new_listing', name: 'New Listing' },
  { id: 'open_house', name: 'Open House' },
  { id: 'sold', name: 'Sold' },
  { id: 'price_reduction', name: 'Price Reduction' },
  { id: 'coming_soon', name: 'Coming Soon' },
  { id: 'general', name: 'General' },
];
const TPL_COLORS = ['#8B5CF6', '#F59E0B', '#10B981', '#EF4444', '#3B82F6', '#EC4899', '#F97316', '#06B6D4'];

const TemplateMetaBar = ({ tplMeta, setTplMeta }) => (
  <div className="flex items-center gap-4 p-3 bg-card border border-border/50 rounded-lg flex-wrap" data-testid="template-meta-bar">
    <div className="flex items-center gap-1.5">
      <span className="text-xs text-muted-foreground">Desc:</span>
      <input
        value={tplMeta.description}
        onChange={(e) => setTplMeta(prev => ({ ...prev, description: e.target.value }))}
        placeholder="Template description..."
        className="h-7 w-56 rounded-md border border-input bg-background px-2 text-xs"
        data-testid="template-desc-input"
      />
    </div>
    <div className="flex items-center gap-1.5">
      <span className="text-xs text-muted-foreground">Category:</span>
      <Select value={tplMeta.category} onValueChange={(v) => setTplMeta(prev => ({ ...prev, category: v }))}>
        <SelectTrigger className="h-7 w-28 text-xs"><SelectValue /></SelectTrigger>
        <SelectContent>
          {TPL_CATEGORIES.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
    <div className="flex items-center gap-1.5">
      <span className="text-xs text-muted-foreground">Icon:</span>
      {TPL_ICONS.map(({ id, Icon }) => (
        <button key={id} onClick={() => setTplMeta(prev => ({ ...prev, icon: id }))} className={`p-1 rounded ${tplMeta.icon === id ? 'bg-primary/15 ring-1 ring-primary' : 'hover:bg-muted/50'}`}>
          <Icon className="h-3.5 w-3.5" />
        </button>
      ))}
    </div>
    <div className="flex items-center gap-1.5">
      <span className="text-xs text-muted-foreground">Color:</span>
      {TPL_COLORS.map(c => (
        <button key={c} onClick={() => setTplMeta(prev => ({ ...prev, color: c }))} className={`w-5 h-5 rounded-full border-2 ${tplMeta.color === c ? 'border-foreground scale-110' : 'border-transparent'}`} style={{ backgroundColor: c }} />
      ))}
    </div>
  </div>
);

export const ReelEditor = ({ compositionId = null, propertyData = null, onSave, mode = 'editor', templateId = null, reelTemplate = null, onBack = null, scheduleSignal = 0 }) => {
  const { handleApiError } = usePlan() || {};
  // Mobile/tablet → tabbed layout (Assets / Preview / Layers) so the 3-col
  // desktop flex doesn't squeeze panels off-screen on narrow viewports.
  const isMobile = useIsMobile(900);
  // Bottom-sheet state on mobile. `null` = no sheet, or one of:
  // 'assets' | 'layers' | 'design' | 'audio'. Tapping a layer in the preview
  // or timeline auto-opens the 'layers' sheet so users can edit properties
  // without hunting through a sidebar.
  const [mobileSheet, setMobileSheet] = useState(null);
  const {
    state: composition, set: setComposition, setQuiet: setCompositionQuiet,
    setLive: setCompositionLive, beginChange: beginHistory,
    undo, redo, pastRef, futureRef,
  } = useHistory({
    name: 'Untitled Reel',
    tracks: [],
    layers: [],
    audio: { music_asset_id: null, music_volume: 0.7, voiceover_text: '', voiceover_voice: 'shimmer', voiceover_volume: 1.0, duck_music_to: 0.2 },
  });
  const canUndo = pastRef.current.length > 0;
  const canRedo = futureRef.current.length > 0;
  const [assets, setAssets] = useState([]);
  const [compId, setCompId] = useState(compositionId);
  const [saving, setSaving] = useState(false);
  const [renderJob, setRenderJob] = useState(null);
  const [rendering, setRendering] = useState(false);
  const [selectedLayerId, setSelectedLayerId] = useState(null);
  const [designId, setDesignId] = useState(null);
  const [showTemplatePicker, setShowTemplatePicker] = useState(mode !== 'template' && !compositionId && !propertyData?.photos?.length && !templateId && !reelTemplate);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [tplMeta, setTplMeta] = useState({
    description: '', category: 'general', color: '#8B5CF6', icon: 'sparkles',
  });
  const [tplId, setTplId] = useState(templateId);
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [publishInitialMode, setPublishInitialMode] = useState('now');
  const pollRef = useRef(null);

  // Resizable timeline dock (desktop). Persisted so the user's split sticks.
  const [timelineHeight, setTimelineHeight] = useState(() => {
    const saved = parseInt(localStorage.getItem('realprop.reel.timelineHeight'), 10);
    return Number.isFinite(saved) ? saved : 260;
  });
  const centerColRef = useRef(null);
  const dividerDragRef = useRef(null);

  const handleDividerDown = useCallback((e) => {
    e.preventDefault();
    const colH = centerColRef.current?.getBoundingClientRect().height || 600;
    dividerDragRef.current = { startY: e.clientY, startH: timelineHeight, colH };
    const onMove = (ev) => {
      const d = dividerDragRef.current;
      if (!d) return;
      // Drag up → taller timeline.
      let next = d.startH + (d.startY - ev.clientY);
      next = Math.max(140, Math.min(Math.round(d.colH * 0.7), next));
      setTimelineHeight(next);
    };
    const onUp = () => {
      dividerDragRef.current = null;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      setTimelineHeight(h => { localStorage.setItem('realprop.reel.timelineHeight', String(h)); return h; });
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }, [timelineHeight]);

  // The top-bar "Schedule" button (in ReelEditorPage) bumps scheduleSignal.
  // Open the publish dialog in Schedule mode — but only once the reel is rendered.
  useEffect(() => {
    if (scheduleSignal > 0) {
      if (renderJob?.status === 'complete') {
        setPublishInitialMode('schedule');
        setShowPublishDialog(true);
      } else {
        toast.error('Render the reel first, then you can schedule it.');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleSignal]);

  // Helper: replace {{price}}, {{address}}, etc. in template strings
  const fillVars = (text, data) => {
    if (!text || !data) return text || '';
    return text
      .replace(/\{\{price\}\}/g, data.price || '')
      .replace(/\{\{address\}\}/g, data.address || '')
      .replace(/\{\{beds\}\}/g, data.beds || '')
      .replace(/\{\{baths\}\}/g, data.baths || '')
      .replace(/\{\{sqft\}\}/g, data.sqft || '')
      .replace(/\{\{open_house_date\}\}/g, data.open_house_date || 'Saturday 1-4 PM')
      .replace(/\{\{property_type\}\}/g, data.property_type || 'Property');
  };

  // Helper: convert overlay position string to canvas x/y
  const posToXY = (pos, width) => {
    const w = width || 500;
    const map = {
      'top-left': { x: 50, y: 60 },
      'top-center': { x: Math.round((1080 - w) / 2), y: 60 },
      'top-right': { x: 1080 - w - 50, y: 60 },
      'center': { x: Math.round((1080 - w) / 2), y: 880 },
      'bottom-left': { x: 50, y: 1680 },
      'bottom-center': { x: 50, y: 1760 },
      'bottom-right': { x: 1080 - w - 50, y: 1760 },
    };
    return map[pos] || { x: 50, y: 50 };
  };

  // Initialize from template structure + property data
  const initFromTemplateAndProperty = async (tplId, tplData, propData) => {
    try {
      // If we have tplData from state, use it; otherwise fetch by ID
      let tpl = tplData;
      if (!tpl && tplId) {
        const res = await apiClient.get(`/reels/templates/${tplId}`);
        tpl = res.data;
      }
      if (!tpl) return;

      const tracksConfig = tpl.tracks_config || tpl.tracks || [];
      const overlaysConfig = tpl.overlays_config || [];
      const audioConfig = tpl.audio_config || tpl.audio || {};
      const photos = propData.photos || [];

      // Build tracks from template config, fill with property photos
      const tracks = tracksConfig.map((tc, i) => ({
        type: 'photo',
        asset_id: photos[i] ? `scraped_${i}` : `tpl_${tc.label || i}`,
        source_url: photos[i] || tc.source_url || null,
        duration: tc.duration || 3,
        animation: tc.animation || 'zoom-in',
        transition: tc.transition || 'fade',
        trim_start: tc.trim_start || 0,
        trim_end: tc.trim_end || 0,
      }));

      const totalDur = tracks.reduce((s, t) => s + t.duration, 0);

      // Build layers from overlays_config with property data injected
      const layers = [];
      if (overlaysConfig.length > 0) {
        overlaysConfig.forEach((ov, i) => {
          const content = fillVars(ov.content, propData);
          if (!content) return;
          const style = ov.style || {};
          const fontSize = style.fontSize || 36;
          const estWidth = Math.min(980, Math.max(300, content.length * fontSize * 0.55));
          const { x, y } = posToXY(ov.position, estWidth);
          layers.push({
            id: `tpl_layer_${i}`,
            type: 'text',
            x, y,
            width: Math.round(estWidth),
            height: Math.round(fontSize * 1.6),
            content,
            fontSize,
            fontColor: style.color || 'white',
            fontWeight: 'bold',
            fontFamily: 'Montserrat',
            textAlign: 'center',
            bgHighlight: style.bgColor ? {
              enabled: true,
              color: style.bgColor.split('@')[0] === 'black' ? '#000000' : (style.bgColor.split('@')[0] || '#000000'),
              opacity: parseFloat(style.bgColor.split('@')[1]) || 0.6,
              padding: 10,
              borderRadius: 6,
            } : undefined,
            opacity: 1,
            rotation: 0,
            start: ov.start || 0,
            end: ov.end === 999 ? totalDur : (ov.end || totalDur),
          });
        });
      } else if (tpl.layers?.length) {
        // User-created template with direct layers
        tpl.layers.forEach((l, i) => {
          layers.push({ ...l, id: `tpl_layer_${i}`, content: fillVars(l.content, propData) });
        });
      }

      // Build voiceover text from template
      const voiceText = fillVars(
        audioConfig.voiceover_prompt || audioConfig.voiceover_text || '',
        propData
      );

      setCompositionQuiet(prev => ({
        ...prev,
        name: propData.address ? `Reel - ${propData.address}` : (tpl.name || 'Property Reel'),
        tracks,
        layers,
        audio: {
          ...prev.audio,
          music_volume: audioConfig.music_volume ?? 0.7,
          voiceover_text: voiceText,
          voiceover_voice: audioConfig.voiceover_voice || 'shimmer',
          duck_music_to: audioConfig.duck_music_to ?? 0.2,
        },
      }));
    } catch (e) {
      console.error('Failed to init from template + property:', e);
      // Fallback: just use property data directly (existing logic will handle it below)
    }
  };

  useEffect(() => {
    if (mode === 'template' && templateId) {
      loadTemplate(templateId);
    } else if (compositionId) {
      loadComposition(compositionId);
    } else if ((templateId || reelTemplate) && propertyData) {
      // Template + Property Data: build tracks from template structure, fill with property
      initFromTemplateAndProperty(templateId, reelTemplate, propertyData);
    } else if (propertyData?.photos?.length) {
      const initialTracks = propertyData.photos.slice(0, 6).map((photo, i) => ({
        type: 'photo', asset_id: `scraped_${i}`, source_url: photo, duration: 3,
        animation: i === 0 ? 'zoom-in' : i % 2 === 0 ? 'pan-left' : 'pan-right',
        transition: 'fade', trim_start: 0, trim_end: 0,
      }));
      const initialLayers = [];
      const totalDur = initialTracks.length * 3;

      // "JUST LISTED" or property_type tag (visible on first clip)
      initialLayers.push({
        id: 'tag_init', type: 'rect', x: 50, y: 50, width: 280, height: 65,
        fill: '#8B5CF6', borderRadius: 10, opacity: 0.9,
        rotation: 0, start: 0, end: 3,
      });
      initialLayers.push({
        id: 'tag_text_init', type: 'text', x: 55, y: 52, width: 270, height: 60,
        content: (propertyData.property_type || 'JUST LISTED').toUpperCase(),
        fontSize: 30, fontColor: 'white', fontWeight: 'bold', fontFamily: 'Montserrat',
        textAlign: 'center', opacity: 1, rotation: 0, start: 0, end: 3,
      });

      // Price (visible throughout)
      if (propertyData.price) {
        initialLayers.push({
          id: 'price_init', type: 'text', x: 50, y: 60, width: 500, height: 80,
          content: propertyData.price, fontSize: 52, fontColor: '#C9A96E', fontWeight: 'bold',
          fontFamily: 'Playfair Display', textAlign: 'left',
          shadow: { color: '#000000', blur: 6, offsetX: 2, offsetY: 2 },
          opacity: 1, rotation: 0, start: 3, end: totalDur,
        });
      }

      // Address (visible throughout)
      if (propertyData.address) {
        initialLayers.push({
          id: 'addr_init', type: 'text', x: 50, y: 1760, width: 980, height: 65,
          content: propertyData.address, fontSize: 36, fontColor: 'white', fontWeight: 'bold',
          fontFamily: 'Montserrat', textAlign: 'left',
          shadow: { color: '#000000', blur: 4, offsetX: 1, offsetY: 1 },
          opacity: 1, rotation: 0, start: 0, end: totalDur,
        });
      }

      // Features strip (beds/baths/sqft — visible on clips 2-4)
      const features = [propertyData.beds && `${propertyData.beds} Beds`, propertyData.baths && `${propertyData.baths} Baths`, propertyData.sqft && `${propertyData.sqft} Sq Ft`].filter(Boolean).join('  |  ');
      if (features) {
        initialLayers.push({
          id: 'features_init', type: 'text', x: 50, y: 1680, width: 980, height: 55,
          content: features, fontSize: 28, fontColor: 'white', fontWeight: 'normal',
          fontFamily: 'Montserrat', textAlign: 'left',
          bgHighlight: { enabled: true, color: '#000000', opacity: 0.5, padding: 10, borderRadius: 6 },
          opacity: 1, rotation: 0, start: 3, end: Math.min(totalDur, 12),
        });
      }

      // CTA text (visible on last clip)
      const lastClipStart = (initialTracks.length - 1) * 3;
      initialLayers.push({
        id: 'cta_init', type: 'text', x: 140, y: 800, width: 800, height: 100,
        content: 'Schedule a Showing', fontSize: 44, fontColor: 'white', fontWeight: 'bold',
        fontFamily: 'Playfair Display', textAlign: 'center',
        shadow: { color: '#000000', blur: 8, offsetX: 2, offsetY: 2 },
        opacity: 1, rotation: 0, start: lastClipStart, end: totalDur,
      });

      // Generate voiceover text
      const voiceText = [
        propertyData.property_type ? `${propertyData.property_type}.` : 'Just listed.',
        propertyData.address ? `Located at ${propertyData.address}.` : '',
        propertyData.price ? `Priced at ${propertyData.price}.` : '',
        features ? `Featuring ${features.replace(/\|/g, ',')}.` : '',
        'Schedule your private showing today.',
      ].filter(Boolean).join(' ');

      setCompositionQuiet(prev => ({
        ...prev,
        name: propertyData.address ? `Reel - ${propertyData.address}` : 'Property Reel',
        tracks: initialTracks,
        layers: initialLayers,
        audio: { ...prev.audio, voiceover_text: voiceText },
        property_data: propertyData,
      }));
    } else if (mode === 'template' && !templateId) {
      setCompositionQuiet(prev => ({ ...prev, name: 'New Template' }));
    }
    loadAssets();
  }, [compositionId, templateId, reelTemplate, mode]);

  const loadComposition = async (id) => {
    try {
      const res = await apiClient.get(`/reels/compositions/${id}`);
      const data = res.data;
      if (data.overlays && !data.layers?.length) {
        data.layers = data.overlays;
      }
      setCompositionQuiet(data);
      setDesignId(data.design_id || null);
    } catch (e) {
      toast.error('Failed to load composition');
    }
  };

  const loadTemplate = async (id) => {
    try {
      const res = await apiClient.get(`/reels/templates/${id}`);
      const data = res.data;
      setCompositionQuiet({
        name: data.name || 'Untitled Template',
        tracks: data.tracks || data.tracks_config?.map(tc => ({
          type: 'photo', asset_id: `tpl_${tc.label}`, source_url: null,
          duration: tc.duration, animation: tc.animation, transition: tc.transition,
          trim_start: 0, trim_end: 0,
        })) || [],
        layers: data.layers || [],
        audio: data.audio || data.audio_config || composition.audio,
      });
      setTplMeta({
        description: data.description || '',
        category: data.category || 'general',
        color: data.color || '#8B5CF6',
        icon: data.icon || 'sparkles',
      });
    } catch (e) {
      toast.error('Failed to load template');
    }
  };

  const loadAssets = async () => {
    try {
      const res = await apiClient.get('/reels/assets');
      setAssets(res.data.assets || []);
    } catch { /* silent */ }
  };

  const handleSave = async () => {
    if (mode === 'template') {
      return handleSaveTemplate();
    }
    setSaving(true);
    try {
      if (compId) {
        await apiClient.put(`/reels/compositions/${compId}`, composition);
        toast.success('Composition saved');
      } else {
        const res = await apiClient.post('/reels/compositions', composition);
        setCompId(res.data.composition_id);
        toast.success('Composition created');
      }
      if (onSave) onSave(composition);
    } catch (e) {
      if (handleApiError && handleApiError(e)) return;
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  const handleSaveTemplate = async () => {
    setSaving(true);
    try {
      const payload = {
        name: composition.name,
        description: tplMeta.description,
        category: tplMeta.category,
        color: tplMeta.color,
        icon: tplMeta.icon,
        tracks: composition.tracks,
        layers: composition.layers,
        audio: composition.audio,
      };
      if (tplId) {
        await apiClient.put(`/reels/templates/${tplId}`, payload);
        toast.success('Template updated');
      } else {
        const res = await apiClient.post('/reels/templates', payload);
        setTplId(res.data.template_id);
        toast.success('Template created');
      }
      if (onSave) onSave();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save template');
    }
    setSaving(false);
  };

  const handleRender = async () => {
    await handleSave();
    if (!compId) { toast.error('Save the composition first'); return; }
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
    setRendering(true);
    try {
      const res = await apiClient.post(`/reels/render/${compId}`);
      setRenderJob(res.data);
      toast.success('Render started! You can keep editing while it processes.');
      startPolling(res.data.job_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start render');
      setRendering(false);
    }
  };

  const startPolling = (jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await apiClient.get(`/reels/render-status/${jobId}`);
        setRenderJob(res.data);
        if (res.data.status === 'complete') {
          clearInterval(pollRef.current);
          setRendering(false);
          toast.success('Reel rendered! Click Download to get your MP4.');
          if ('Notification' in window && Notification.permission === 'granted') {
            new Notification('RealProp — Reel Ready!', { body: `Reel rendered (${res.data.duration}s).`, icon: '/favicon.ico', tag: `render-${jobId}` });
          }
        } else if (res.data.status === 'failed') {
          clearInterval(pollRef.current);
          setRendering(false);
          toast.error(`Render failed: ${res.data.error || 'Unknown error'}`);
        }
      } catch { /* retry */ }
    }, 3000);
  };

  useEffect(() => { return () => { if (pollRef.current) clearInterval(pollRef.current); }; }, []);

  const handleDownload = async () => {
    if (!renderJob?.download_url) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}${renderJob.download_url}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `realprop_reel_${renderJob.job_id || 'video'}.mp4`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Download failed: ' + e.message);
    }
  };

  const updateTracks = useCallback((t) => setComposition(prev => ({ ...prev, tracks: t })), []);
  const updateLayers = useCallback((l) => setComposition(prev => ({ ...prev, layers: l })), []);
  const updateAudio = useCallback((a) => setComposition(prev => ({ ...prev, audio: { ...prev.audio, ...a } })), []);

  const updateTrackTransform = useCallback((trackIdx, transform) => {
    setComposition(prev => ({
      ...prev,
      tracks: prev.tracks.map((t, i) => i === trackIdx ? { ...t, transform } : t),
    }));
  }, []);

  const audioSegments = composition.audio?.segments || [];
  const updateAudioSegments = useCallback((segs) => {
    setComposition(prev => ({ ...prev, audio: { ...prev.audio, segments: segs } }));
  }, []);
  const addAudioSegment = useCallback((seg) => {
    setComposition(prev => ({
      ...prev,
      audio: { ...prev.audio, segments: [...(prev.audio.segments || []), seg] },
    }));
  }, []);

  const addBlankSlide = useCallback(() => {
    setComposition(prev => ({
      ...prev,
      tracks: [...prev.tracks, {
        type: 'photo',
        asset_id: `blank_${Date.now()}`,
        source_url: null,
        duration: 3,
        animation: 'none',
        transition: 'fade',
        trim_start: 0,
        trim_end: 0,
      }],
    }));
  }, []);

  const updateLayerProps = useCallback((layerId, updates) => {
    setComposition(prev => ({
      ...prev,
      layers: prev.layers.map(l => l.id === layerId ? { ...l, ...updates } : l),
    }));
  }, []);

  // Live (no-history) layer update for drag/resize/rotate gestures. The single
  // undo snapshot is taken by beginHistory() on the first move of the gesture.
  const updateLayerPropsLive = useCallback((layerId, updates) => {
    setCompositionLive(prev => ({
      ...prev,
      layers: prev.layers.map(l => l.id === layerId ? { ...l, ...updates } : l),
    }));
  }, [setCompositionLive]);

  // Stable callback so memoized AssetPanel doesn't re-render on time ticks.
  const addTrack = useCallback((track) => {
    setComposition(prev => ({ ...prev, tracks: [...prev.tracks, track] }));
  }, []);

  // DnD state
  const [activeDropTarget, setActiveDropTarget] = useState(null);
  const [activeDragData, setActiveDragData] = useState(null);

  const sensors = useSensors(
    // Desktop: mouse / trackpad — start dragging after a small 5px move.
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    // Touch: require a brief 200ms hold before drag begins so tapping a clip
    // (to select) and scrolling the timeline both still work without false-
    // positive drags. Tolerance lets the finger drift up to 5px during the
    // hold without cancelling.
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
  );

  const handleDragStart = (event) => {
    setActiveDragData(event.active.data.current);
  };

  const handleDragOver = (event) => {
    const overId = event.over?.id;
    setActiveDropTarget(overId || null);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveDropTarget(null);
    setActiveDragData(null);

    if (!over) return;

    const activeData = active.data.current;
    const overData = over.data.current;

    // Case 1: Asset dropped from AssetPanel onto a timeline clip → replace
    if (activeData?.type === 'asset' && overData?.type === 'timeline-clip') {
      const replaceIdx = overData.index;
      const newTrack = { ...activeData.track };
      setComposition(prev => ({
        ...prev,
        tracks: prev.tracks.map((t, i) => i === replaceIdx ? { ...newTrack, transition: t.transition } : t),
      }));
      toast.success(`Replaced clip #${replaceIdx + 1}`);
      return;
    }

    // Case 2: Asset dropped onto the add zone → add new track
    if (activeData?.type === 'asset' && over.id === 'timeline-add-zone') {
      const newTrack = { ...activeData.track };
      updateTracks([...composition.tracks, newTrack]);
      toast.success('Added to timeline');
      return;
    }

    // Case 3: Sortable reorder within timeline
    if (activeData?.type === 'timeline-clip' && overData?.type === 'timeline-clip') {
      const oldIndex = activeData.index;
      const newIndex = overData.index;
      if (oldIndex !== newIndex) {
        const reordered = arrayMove(composition.tracks, oldIndex, newIndex);
        updateTracks(reordered);
      }
      return;
    }
  };

  const handleDragCancel = () => {
    setActiveDropTarget(null);
    setActiveDragData(null);
  };

  // ========== Keyboard Shortcuts ==========
  const deleteSelectedLayer = useCallback(() => {
    if (!selectedLayerId) return;
    setComposition(prev => ({
      ...prev,
      layers: prev.layers.filter(l => l.id !== selectedLayerId),
    }));
    setSelectedLayerId(null);
  }, [selectedLayerId, setComposition]);

  const duplicateSelectedLayer = useCallback(() => {
    if (!selectedLayerId) return;
    setComposition(prev => {
      const src = prev.layers.find(l => l.id === selectedLayerId);
      if (!src) return prev;
      const newId = `layer_${Date.now().toString(36)}`;
      return {
        ...prev,
        layers: [...prev.layers, { ...src, id: newId, x: src.x + 20, y: src.y + 20 }],
      };
    });
  }, [selectedLayerId, setComposition]);

  useEffect(() => {
    const handler = (e) => {
      // Skip if user is typing in an input/textarea
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target.isContentEditable) return;

      const isMod = e.ctrlKey || e.metaKey;

      // Ctrl+Z — Undo
      if (isMod && !e.shiftKey && e.key === 'z') {
        e.preventDefault();
        undo();
        return;
      }
      // Ctrl+Shift+Z or Ctrl+Y — Redo
      if ((isMod && e.shiftKey && e.key === 'z') || (isMod && e.key === 'y')) {
        e.preventDefault();
        redo();
        return;
      }
      // Ctrl+S — Save
      if (isMod && e.key === 's') {
        e.preventDefault();
        handleSave();
        return;
      }
      // Ctrl+D — Duplicate layer
      if (isMod && e.key === 'd') {
        e.preventDefault();
        duplicateSelectedLayer();
        return;
      }
      // Delete / Backspace — Delete selected layer
      if (e.key === 'Delete' || e.key === 'Backspace') {
        deleteSelectedLayer();
        return;
      }
      // Escape — Deselect
      if (e.key === 'Escape') {
        setSelectedLayerId(null);
        return;
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [undo, redo, deleteSelectedLayer, duplicateSelectedLayer]);

  const handleTemplateApplied = (compData) => {
    if (compData) {
      setCompId(compData.composition_id);
      setCompositionQuiet({
        name: compData.name,
        tracks: compData.tracks || [],
        layers: compData.layers || compData.overlays || [],
        audio: compData.audio || composition.audio,
      });
      setDesignId(compData.design_id || null);
    }
    setShowTemplatePicker(false);
  };

  const handleDesignApplied = useCallback((newLayers, newDesignId) => {
    updateLayers(newLayers);
    setDesignId(newDesignId);
  }, [updateLayers]);

  const selectedLayer = composition.layers.find(l => l.id === selectedLayerId) || null;
  const totalDuration = composition.tracks.reduce((sum, t) => sum + getEffDur(t), 0);

  if (showTemplatePicker) {
    return (
      <div className="flex flex-col h-full gap-4" data-testid="reel-editor">
        <ReelTemplatePicker propertyData={propertyData} onTemplateApplied={handleTemplateApplied} />
      </div>
    );
  }

  return (
    <>
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
    <div className="flex flex-col h-full gap-4" data-testid="reel-editor">
      {/* Top bar */}
      <div className="flex items-center justify-between p-3 bg-card border border-border/50 rounded-lg">
        <div className="flex items-center gap-3">
          {onBack && (
            <Button variant="ghost" size="sm" onClick={onBack} data-testid="editor-back-btn">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          )}
          <Film className="h-5 w-5 text-purple-500" />
          <Input value={composition.name} onChange={(e) => setComposition(prev => ({ ...prev, name: e.target.value }))} className="h-8 w-64 text-sm font-medium" data-testid="reel-name-input" />
          <span className="text-xs text-muted-foreground">{totalDuration.toFixed(1)}s / 180s max</span>
          {mode === 'template' && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-500 font-medium">Template Mode</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-0.5 mr-2 border-r border-border/50 pr-2">
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={undo} disabled={!canUndo} title="Undo (Ctrl+Z)" data-testid="undo-btn">
              <Undo2 className="h-3.5 w-3.5" />
            </Button>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={redo} disabled={!canRedo} title="Redo (Ctrl+Shift+Z)" data-testid="redo-btn">
              <Redo2 className="h-3.5 w-3.5" />
            </Button>
          </div>
          <Button variant="outline" size="sm" onClick={handleSave} disabled={saving} data-testid="save-composition-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
            {mode === 'template' ? (tplId ? 'Update Template' : 'Save Template') : 'Save'}
          </Button>
          {mode !== 'template' && (
            <>
              <Button size="sm" onClick={handleRender} disabled={rendering || composition.tracks.length === 0} className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700" data-testid="render-btn">
                {rendering ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Film className="h-4 w-4 mr-1" />}
                {rendering ? 'Rendering...' : 'Render MP4'}
              </Button>
              {renderJob?.status === 'complete' && (
                <>
                  <Button size="sm" variant="outline" onClick={handleDownload} data-testid="download-btn">Download MP4</Button>
                  <Button size="sm" variant="outline" onClick={() => { setPublishInitialMode('now'); setShowPublishDialog(true); }} className="gap-1" data-testid="publish-btn">
                    <Send className="h-3.5 w-3.5" /> Publish
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setPublishInitialMode('schedule'); setShowPublishDialog(true); }} className="gap-1" data-testid="reel-schedule-btn">
                    <Clock className="h-3.5 w-3.5" /> Schedule
                  </Button>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Template metadata panel */}
      {mode === 'template' && (
        <TemplateMetaBar tplMeta={tplMeta} setTplMeta={setTplMeta} />
      )}

      {/* Render status */}
      {renderJob && renderJob.status !== 'complete' && renderJob.status !== 'failed' && (
        <div className="p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg" data-testid="render-status">
          <div className="flex items-center gap-3">
            <Loader2 className="h-4 w-4 animate-spin text-purple-500 shrink-0" />
            <span className="text-sm font-medium" data-testid="render-progress-message">
              {renderJob.progress_message || 'Rendering your reel…'}
            </span>
            <span className="text-xs text-muted-foreground ml-auto hidden sm:inline">You can keep editing</span>
          </div>
          {/* Phase steps */}
          <div className="flex items-center gap-1.5 mt-2">
            {['preparing', 'downloading', 'encoding', 'finalizing'].map((stage, idx) => {
              const order = ['preparing', 'downloading', 'encoding', 'finalizing'];
              const current = order.indexOf(renderJob.progress_stage || 'preparing');
              const done = idx <= current;
              return (
                <div
                  key={stage}
                  className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${done ? 'bg-purple-500' : 'bg-purple-500/20'}`}
                  data-testid={`render-phase-${stage}`}
                />
              );
            })}
          </div>
        </div>
      )}
      {renderJob?.status === 'failed' && (
        <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive">
          Render failed: {renderJob.error}
        </div>
      )}

      {/* Canva-style text formatting toolbar */}
      {selectedLayer?.type === 'text' && (
        <CanvaTextToolbar layer={selectedLayer} onUpdate={updateLayerProps} />
      )}

      {/* Main layout — 3-column on desktop, tabbed on mobile/narrow tablet */}
      {isMobile ? (
        <>
          {/* Canvas-first mobile layout: Preview + Timeline always visible.
              Asset library / Layers / Design / Audio open in bottom-sheet
              drawers — matches the Canva-style UX of the post editor. */}
          <div className="flex-1 flex flex-col gap-2 min-h-0 overflow-y-auto" data-testid="reel-mobile-canvas">
            <div className="flex-shrink-0" style={{ minHeight: '55vh' }}>
              <PreviewPlayer
                tracks={composition.tracks}
                layers={composition.layers}
                onTimeUpdate={setCurrentTime}
                onPlayingChange={setIsPlaying}
                selectedLayerId={selectedLayerId}
                onSelectLayer={(id) => { setSelectedLayerId(id); if (id) setMobileSheet('layers'); }}
                onUpdateLayer={updateLayerProps}
                onUpdateLayerLive={updateLayerPropsLive}
                onLayerGestureStart={beginHistory}
                propertyData={propertyData}
                onUpdateTrackTransform={updateTrackTransform}
                seekTime={currentTime}
                audioSegments={audioSegments}
                assets={assets}
              />
            </div>
            <Timeline tracks={composition.tracks} onTracksChange={updateTracks} activeDropTarget={activeDropTarget} currentTime={currentTime} onSeek={setCurrentTime} onAddBlank={addBlankSlide}
              audioSegments={audioSegments} onAudioSegmentsChange={updateAudioSegments} isPlaying={isPlaying}
              layers={composition.layers} selectedLayerId={selectedLayerId} onSelectLayer={(id) => { setSelectedLayerId(id); if (id) setMobileSheet('layers'); }} onLayerUpdate={updateLayerProps} />
          </div>

          {/* Bottom toolbar — sticky inside the editor's own scrolling area so
              it's always visible without needing the user to scroll to find it.
              Using `sticky` rather than `fixed` keeps it bounded by the parent
              editor container (`ReelEditorPage` uses `h-[100dvh]` + `overflow-hidden`)
              instead of escaping to the viewport which on iOS Safari can land
              behind the dynamic browser chrome. */}
          <div className="sticky bottom-0 inset-x-0 z-40 bg-card border-t border-border/50 flex overflow-x-auto no-scrollbar -mx-3 sm:mx-0" data-testid="reel-mobile-toolbar">
            {[
              { id: 'assets', label: 'Assets', icon: Image },
              { id: 'layers', label: 'Layers', icon: Layers },
              { id: 'design', label: 'Design', icon: Palette },
              { id: 'audio', label: 'Audio', icon: Music },
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => setMobileSheet(id)}
                className="flex-1 min-w-[80px] flex flex-col items-center justify-center gap-1 py-2 active:bg-muted"
                data-testid={`reel-toolbar-${id}`}
              >
                <Icon className="h-5 w-5" />
                <span className="text-[10px] font-medium">{label}</span>
              </button>
            ))}
          </div>

          {/* First-visit coach mark — auto-dismisses, then never reappears */}
          <MobileCoachMark
            storageKey="realprop.reel.mobile-coach.v1"
            label="Tap an icon to open Assets, Layers, Design or Audio"
            bottomOffsetPx={64}
          />

          {/* Bottom sheets */}
          {mobileSheet && (
            <div className="fixed inset-0 z-50 flex flex-col" data-testid="reel-mobile-sheet">
              <div className="flex-1 bg-black/40" onClick={() => setMobileSheet(null)} />
              <div className="bg-card rounded-t-2xl shadow-2xl max-h-[85vh] flex flex-col">
                <div className="flex items-center justify-between p-3 border-b border-border/50 sticky top-0 bg-card rounded-t-2xl">
                  <div className="text-sm font-semibold capitalize">{mobileSheet}</div>
                  <button onClick={() => setMobileSheet(null)} className="p-2 -mr-2 rounded-md hover:bg-muted" aria-label="Close" data-testid="reel-sheet-close">
                    <ChevronDown className="h-5 w-5" />
                  </button>
                </div>
                <div className="overflow-y-auto p-3 space-y-3">
                  {mobileSheet === 'assets' && (
                    <AssetPanel assets={assets} tracks={composition.tracks} onAssetsChange={loadAssets} onAddTrack={(track) => { updateTracks([...composition.tracks, track]); setMobileSheet(null); }} />
                  )}
                  {mobileSheet === 'layers' && (
                    <>
                      <LayerPanel layers={composition.layers} selectedId={selectedLayerId} onSelect={setSelectedLayerId} onLayersChange={updateLayers} propertyData={propertyData} />
                      <PropertyInspector layer={selectedLayer} onUpdate={updateLayerProps} />
                    </>
                  )}
                  {mobileSheet === 'design' && (
                    <DesignPicker compositionId={compId} propertyData={propertyData} currentDesignId={designId} onDesignApplied={(...args) => { handleDesignApplied(...args); setMobileSheet(null); }} />
                  )}
                  {mobileSheet === 'audio' && (
                    <AudioMixer audio={composition.audio} onAudioChange={updateAudio} assets={assets} onAssetsChange={loadAssets} onAddAudioSegment={addAudioSegment} />
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
      <div className="flex-1 flex gap-4 min-h-0">
        {/* Left: Asset panel */}
        <div className="flex-shrink-0 min-h-0 overflow-y-auto editor-scroll">
          <MAssetPanel assets={assets} tracks={composition.tracks} onAssetsChange={loadAssets} onAddTrack={addTrack} />
        </div>

        {/* Center: Preview (fills) + draggable divider + docked Timeline */}
        <div ref={centerColRef} className="flex-1 flex flex-col min-w-0 min-h-0">
          <div className="flex-1 min-h-0 overflow-hidden flex items-center justify-center">
            <PreviewPlayer
              tracks={composition.tracks}
              layers={composition.layers}
              onTimeUpdate={setCurrentTime}
              onPlayingChange={setIsPlaying}
              selectedLayerId={selectedLayerId}
              onSelectLayer={setSelectedLayerId}
              onUpdateLayer={updateLayerProps}
              onUpdateLayerLive={updateLayerPropsLive}
              onLayerGestureStart={beginHistory}
              propertyData={propertyData}
              onUpdateTrackTransform={updateTrackTransform}
              seekTime={currentTime}
              audioSegments={audioSegments}
              assets={assets}
            />
          </div>
          {/* Draggable divider */}
          <div
            onPointerDown={handleDividerDown}
            className="h-3 flex items-center justify-center cursor-row-resize group flex-shrink-0 touch-none"
            data-testid="timeline-divider"
            title="Drag to resize the timeline"
          >
            <div className="w-16 h-1 rounded-full bg-border group-hover:bg-purple-400 transition-colors" />
          </div>
          {/* Docked, vertically-scrollable timeline */}
          <div
            className="overflow-y-auto editor-scroll flex-shrink-0"
            style={{ height: timelineHeight }}
            data-testid="timeline-dock"
          >
            <Timeline tracks={composition.tracks} onTracksChange={updateTracks} activeDropTarget={activeDropTarget} currentTime={currentTime} onSeek={setCurrentTime} onAddBlank={addBlankSlide}
              audioSegments={audioSegments} onAudioSegmentsChange={updateAudioSegments} isPlaying={isPlaying}
              layers={composition.layers} selectedLayerId={selectedLayerId} onSelectLayer={setSelectedLayerId} onLayerUpdate={updateLayerProps} />
          </div>
        </div>

        {/* Right: Layers + Inspector + Design + Audio */}
        <div className="w-72 flex-shrink-0 flex flex-col gap-3 overflow-y-auto editor-scroll min-h-0">
          <MLayerPanel layers={composition.layers} selectedId={selectedLayerId} onSelect={setSelectedLayerId} onLayersChange={updateLayers} propertyData={propertyData} />
          <MPropertyInspector layer={selectedLayer} onUpdate={updateLayerProps} />
          <MDesignPicker compositionId={compId} propertyData={propertyData} currentDesignId={designId} onDesignApplied={handleDesignApplied} />
          <MAudioMixer audio={composition.audio} onAudioChange={updateAudio} assets={assets} onAssetsChange={loadAssets} onAddAudioSegment={addAudioSegment} />
        </div>
      </div>
      )}

      {/* Drag overlay — show thumbnail preview */}
      <DragOverlay dropAnimation={null}>
        {activeDragData?.type === 'asset' && (
          <div className="flex items-center gap-2 p-1.5 bg-card border-2 border-purple-500 rounded-lg shadow-2xl text-xs pointer-events-none" style={{ maxWidth: 180 }}>
            {activeDragData.track?.source_url ? (
              <img
                src={activeDragData.track.source_url}
                alt=""
                className="w-12 h-12 rounded object-cover flex-shrink-0"
              />
            ) : (
              <div className="w-12 h-12 rounded bg-muted/40 flex items-center justify-center flex-shrink-0">
                {activeDragData.asset?.asset_type === 'video' ? (
                  <Video className="h-5 w-5 text-blue-400" />
                ) : (
                  <Image className="h-5 w-5 text-purple-400" />
                )}
              </div>
            )}
            <div className="min-w-0">
              <p className="font-medium truncate">{activeDragData.asset?.filename || 'Asset'}</p>
              <p className="text-[10px] text-muted-foreground capitalize">{activeDragData.asset?.asset_type || 'media'}</p>
            </div>
          </div>
        )}
      </DragOverlay>
    </div>
    </DndContext>

    {/* Publish Dialog */}
    <PublishDialog
      isOpen={showPublishDialog}
      onClose={() => setShowPublishDialog(false)}
      publicVideoUrl={renderJob?.public_video_url}
      compositionName={composition.name}
      propertyData={propertyData || composition?.property_data}
      initialMode={publishInitialMode}
    />
    <style>{`
      .editor-scroll::-webkit-scrollbar { width: 10px; height: 10px; }
      .editor-scroll::-webkit-scrollbar-track { background: transparent; }
      .editor-scroll::-webkit-scrollbar-thumb { background: rgba(120,120,135,0.45); border-radius: 6px; border: 2px solid transparent; background-clip: content-box; }
      .editor-scroll::-webkit-scrollbar-thumb:hover { background: rgba(120,120,135,0.7); background-clip: content-box; }
      .editor-scroll { scrollbar-width: thin; scrollbar-color: rgba(120,120,135,0.5) transparent; }
    `}</style>
    </>
  );
};

export default ReelEditor;
